import React from 'react';
import SimpleDropdownPopup from '../ui/SimpleDropdownPopup';
import { ChangeIndicator } from '../common/ChangeIndicator';

interface Unit {
  id: string;
  unitDescriptor: string;
  packagingUnit: string;
  quantityOfBaseUnits: string;
  grossWeight: string;
  grossWeightUnit: string;
  dimensionUnit: string;
  width: string;
  height: string;
  depth: string;
  volumeCM3: string;
  isPieceWeightArticle: boolean;
  isTradeItemBaseUnit: boolean;
}

interface UnitsTableProps {
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  isSupplierMode?: boolean;
  originalData?: any;
  articleNumber?: string;
}

export const UnitsTable: React.FC<UnitsTableProps> = ({ 
  data, 
  mode,
  onChange, 
  isSupplierMode = false,
  originalData,
  articleNumber
}) => {
  // **UTILITY FUNCTIONS FOR CHANGE DETECTION**
  const getFieldValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const isFieldChanged = (fieldPath: string): boolean => {
    console.log(`[UNITS_FIELD_CHANGED] DEBUGGING ${fieldPath}:`, {
      hasOriginalData: !!originalData,
      hasData: !!data,
      originalData: originalData,
      data: data
    });
    
    if (!originalData || !data) return false;
    
    // Use submissionSnapshot as baseline if available, otherwise use originalData
    const baselineData = data?.submissionSnapshot || originalData;
    
    console.log(`[UNITS_FIELD_CHANGED] ${fieldPath} - Baseline data:`, baselineData);
    console.log(`[UNITS_FIELD_CHANGED] ${fieldPath} - Current data:`, data);
    
    // Special handling for units array
    if (fieldPath === 'units') {
      const originalUnits = getFieldValue(baselineData, fieldPath) || [];
      const currentUnits = getFieldValue(data, fieldPath) || [];
      
      // Filter out auto-generated and empty units from comparison
      const filterUnits = (units: any[]) => {
        return units.filter(unit => {
          // Exclude if auto-generated and still empty
          if (unit._isAutoGenerated && unit._isEmpty) {
            // Check if unit is actually still empty (no meaningful data filled)
            const hasFilledData = unit.quantityOfBaseUnits && unit.quantityOfBaseUnits !== '0' && unit.quantityOfBaseUnits !== '' ||
                                 unit.grossWeight && unit.grossWeight !== '' ||
                                 unit.width && unit.width !== '' ||
                                 unit.height && unit.height !== '' ||
                                 unit.depth && unit.depth !== '' ||
                                 unit.packagingUnit && unit.packagingUnit !== '';
            
            return hasFilledData; // Only include if user has filled actual data
          }
          return true; // Include all non-auto-generated units
        });
      };
      
      const filteredOriginal = filterUnits(originalUnits);
      const filteredCurrent = filterUnits(currentUnits);
      
      if (filteredOriginal.length !== filteredCurrent.length) return true;
      return filteredOriginal.some((item, index) => 
        JSON.stringify(item) !== JSON.stringify(filteredCurrent[index])
      );
    }
    
    // Special handling for individual unit field changes (e.g., 'units[0].quantityOfBaseUnits')
    if (fieldPath.match(/^units\[\d+\]\./)) {
      const currentUnits = getFieldValue(data, 'units') || [];
      const baselineUnits = getFieldValue(baselineData, 'units') || [];
      
      console.log(`[UNITS_FIELD_CHANGED] ${fieldPath} - Units comparison:`, {
        currentUnits,
        baselineUnits
      });
      
      // Extract unit index and field from fieldPath like 'units[0].quantityOfBaseUnits'
      const match = fieldPath.match(/^units\[(\d+)\]\.(.+)$/);
      if (match) {
        const unitIndex = parseInt(match[1]);
        const fieldName = match[2];
        const currentUnit = currentUnits[unitIndex];
        const baselineUnit = baselineUnits[unitIndex];
        
        console.log(`[UNITS_FIELD_CHANGED] ${fieldPath} - Field comparison:`, {
          unitIndex,
          fieldName,
          currentUnit,
          baselineUnit
        });
        
        // SIMPLIFIED: Just compare the field values directly without auto-generated logic
        // The auto-generated logic was preventing change detection
        const currentValue = currentUnit?.[fieldName];
        const baselineValue = baselineUnit?.[fieldName];
        
        const result = String(baselineValue || '') !== String(currentValue || '');
        
        console.log(`[UNITS_FIELD_CHANGED] ${fieldPath} - RESULT:`, {
          currentValue,
          baselineValue,
          result
        });
        
        return result;
      }
    }
    
    const originalValue = getFieldValue(baselineData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    // Handle null/undefined cases
    if (!originalValue && !currentValue) return false;
    if (!originalValue && currentValue) return true;
    if (originalValue && !currentValue) return true;
    
    // For arrays
    if (Array.isArray(originalValue) && Array.isArray(currentValue)) {
      if (originalValue.length !== currentValue.length) return true;
      return originalValue.some((item, index) => 
        JSON.stringify(item) !== JSON.stringify(currentValue[index])
      );
    }
    
    // For objects
    if (typeof originalValue === 'object' && typeof currentValue === 'object') {
      return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
    }
    
    // For primitive values
    return originalValue !== currentValue;
  };

  const isFieldNew = (fieldPath: string): boolean => {
    if (!originalData || !data) return false;
    
    // IMPORTANT: If a submissionSnapshot exists, the field can NEVER be "new" - only "changed"
    // submissionSnapshot means the supplier has already submitted data before
    const hasSubmissionSnapshot = data?.submissionSnapshot;
    if (hasSubmissionSnapshot) {
      return false; // Never "new" if already submitted before
    }
    
    // Special handling for units array
    if (fieldPath === 'units') {
      const originalUnits = getFieldValue(originalData, fieldPath) || [];
      const currentUnits = getFieldValue(data, fieldPath) || [];
      
      // Check if there are new meaningful units (not auto-generated empty ones)
      const hasNewMeaningfulUnits = currentUnits.some((unit: any) => {
        // Skip auto-generated units that are still empty
        if (unit._isAutoGenerated && unit._isEmpty) {
          const hasFilledData = unit.quantityOfBaseUnits && unit.quantityOfBaseUnits !== '0' && unit.quantityOfBaseUnits !== '' ||
                               unit.grossWeight && unit.grossWeight !== '' ||
                               unit.width && unit.width !== '' ||
                               unit.height && unit.height !== '' ||
                               unit.depth && unit.depth !== '' ||
                               unit.packagingUnit && unit.packagingUnit !== '';
          return hasFilledData; // Only consider it "new" if user filled actual data
        }
        return true; // Non-auto-generated units are always considered meaningful
      });
      
      return originalUnits.length === 0 && hasNewMeaningfulUnits;
    }
    
    // Special handling for individual unit field changes (e.g., 'units[0].quantityOfBaseUnits')
    if (fieldPath.match(/^units\[\d+\]\./)) {
      const currentUnits = getFieldValue(data, 'units') || [];
      const originalUnits = getFieldValue(originalData, 'units') || [];
      
      // Extract unit index and field from fieldPath like 'units[0].quantityOfBaseUnits'
      const match = fieldPath.match(/^units\[(\d+)\]\.(.+)$/);
      if (match) {
        const unitIndex = parseInt(match[1]);
        const fieldName = match[2];
        const currentUnit = currentUnits[unitIndex];
        const originalUnit = originalUnits[unitIndex];
        
        // SIMPLIFIED: Just compare the field values directly without auto-generated logic
        const originalValue = originalUnit?.[fieldName];
        const currentValue = currentUnit?.[fieldName];
        
        const originalEmpty = !originalValue || originalValue === '' || originalValue === null || originalValue === '0';
        const currentHasValue = currentValue && currentValue !== '' && currentValue !== null && currentValue !== '0';
        
        return originalEmpty && currentHasValue;
      }
    }
    
    const originalValue = getFieldValue(originalData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    // Field is new if original was empty/null and current has a value
    const originalEmpty = !originalValue || originalValue === '' || originalValue === null;
    const currentHasValue = currentValue && currentValue !== '' && currentValue !== null;
    
    return originalEmpty && currentHasValue;
  };

  // **ENHANCED UTILITY FUNCTION FOR RENDERING FIELDS WITH CHANGE HIGHLIGHTING**
  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Debug-Ausgaben
    console.log(`[UNITS_RENDER_FIELD_WITH_HISTORY] ${fieldName}:`, {
      isSupplierMode,
      hasOriginalData: !!originalData,
      hasArticleNumber: !!articleNumber,
      fieldPath,
      mode
    });
    
    // Change tracking only for Admin (not Supplier mode) AND only in view mode
    if (isSupplierMode || !originalData || !articleNumber || mode === 'edit') {
      console.log(`[UNITS_RENDER_FIELD_WITH_HISTORY] ${fieldName}: Returning plain content (isSupplierMode: ${isSupplierMode}, mode: ${mode})`);
      return content;
    }
    
    const hasChanges = isFieldChanged(fieldPath);
    const isNew = isFieldNew(fieldPath);
    
    console.log(`[UNITS_CHANGE_INDICATOR] ${fieldName}:`, {
      hasChanges,
      isNew,
      fieldPath,
      articleNumber,
      mode
    });
    
    return (
      <ChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        hasChanges={hasChanges}
        isNew={isNew}
        articleNumber={articleNumber}
        className="overflow-visible"
        positionOutside={true}
      >
        {content}
      </ChangeIndicator>
    );
  };
  // Initialisiere 4 vorgefüllte Einheiten wenn keine vorhanden sind
  React.useEffect(() => {
    if (!data?.units || data.units.length === 0) {
      const predefinedUnits = [
        'Basiseinheit (Stück)',
        'Karton',
        'Lage',
        'Palette'
      ];
      
      const initialUnits = predefinedUnits.map((unitType, index) => ({
        id: `initial-${index}`,
        unitDescriptor: unitType,
        packagingUnit: '',
        quantityOfBaseUnits: '0',
        grossWeight: '',
        grossWeightUnit: 'g',
        dimensionUnit: 'cm',
        width: '',
        height: '',
        depth: '',
        volumeCM3: '',
        isPieceWeightArticle: index === 0, // Nur Basiseinheit ist als Basisartikel markiert
        isTradeItemBaseUnit: index === 0,
        _isAutoGenerated: true, // WICHTIG: Markiere als automatisch generiert
        _isEmpty: true // Markiere als leer (Platzhalter)
      }));
      onChange('units', initialUnits);
    }
  }, []);

  const addNewUnit = () => {
    const newUnit: Unit = {
      id: Date.now().toString(),
      unitDescriptor: '',
      packagingUnit: '',
      quantityOfBaseUnits: '0',
      grossWeight: '',
      grossWeightUnit: 'g',
      dimensionUnit: 'cm',
      width: '',
      height: '',
      depth: '',
      volumeCM3: '',
      isPieceWeightArticle: false,
      isTradeItemBaseUnit: false
    };
    const currentUnits = data?.units || [];
    onChange('units', [...currentUnits, newUnit]);
  };

  const updateUnit = (index: number, updatedUnit: Partial<Unit>) => {
    const updatedUnits = [...(data?.units || [])];
    updatedUnits[index] = { ...updatedUnits[index], ...updatedUnit };
    
    // Check if auto-generated unit is no longer empty
    const unit = updatedUnits[index];
    if (unit._isAutoGenerated && unit._isEmpty) {
      // Check if user has entered meaningful data
      const hasFilledData = unit.quantityOfBaseUnits && unit.quantityOfBaseUnits !== '0' && unit.quantityOfBaseUnits !== '' ||
                           unit.grossWeight && unit.grossWeight !== '' ||
                           unit.width && unit.width !== '' ||
                           unit.height && unit.height !== '' ||
                           unit.depth && unit.depth !== '' ||
                           unit.packagingUnit && unit.packagingUnit !== '';
      
      if (hasFilledData) {
        updatedUnits[index]._isEmpty = false; // Mark as no longer empty
      }
    }
    
    // Automatische Volumenberechnung
    if (updatedUnit.width !== undefined || updatedUnit.height !== undefined || updatedUnit.depth !== undefined ||
        updatedUnit.dimensionUnit !== undefined) {
      
      const convertToCm = (value: string, unit: string): number => {
        const numValue = parseFloat(value) || 0;
        switch (unit) {
          case 'mm': return numValue / 10;
          case 'cm': return numValue;
          case 'm': return numValue * 100;
          default: return numValue;
        }
      };
      
      const widthInCm = convertToCm(unit.width, unit.dimensionUnit || 'cm');
      const heightInCm = convertToCm(unit.height, unit.dimensionUnit || 'cm');
      const depthInCm = convertToCm(unit.depth, unit.dimensionUnit || 'cm');
      
      if (widthInCm > 0 && heightInCm > 0 && depthInCm > 0) {
        updatedUnits[index].volumeCM3 = (widthInCm * heightInCm * depthInCm).toString();
      } else {
        updatedUnits[index].volumeCM3 = '';
      }
    }
    
    onChange('units', updatedUnits);
  };

  const deleteUnit = (index: number) => {
    const updatedUnits = data?.units?.filter((_: any, i: number) => i !== index) || [];
    onChange('units', updatedUnits);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h3 className="text-base font-semibold text-gray-900">Artikeleinheiten</h3>
        <button
          type="button"
          onClick={addNewUnit}
          className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          + Hinzufügen
        </button>
      </div>
      
      {/* Table Container */}
      <div className="flex-1 p-3">
        <div className="w-full border border-gray-300 rounded-lg overflow-hidden">
          <table className="w-full border-collapse bg-white relative">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="w-[13%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Einheit
                </th>
                <th className="w-[13%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Verpackung
                </th>
                <th className="w-[8%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Menge
                </th>
                <th className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Gewicht
                </th>
                <th className="w-[6%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Einh.
                </th>
                <th className="w-[7%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Breite
                </th>
                <th className="w-[7%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Höhe
                </th>
                <th className="w-[7%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Tiefe
                </th>
                <th className="w-[8%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Einheit
                </th>
                <th className="w-[8%] px-2 py-2 text-left text-xs font-medium text-gray-700">
                  Volumen
                </th>
                <th className="w-[10%] px-2 py-2 text-center text-xs font-medium text-gray-700">
                  Basisartikel
                </th>
                <th className="w-[6%] px-2 py-2 text-center text-xs font-medium text-gray-700">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {data?.units && data.units.length > 0 ? (
                data.units.map((unit: Unit, index: number) => (
                  <tr key={unit.id || index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    {/* Einheitenbeschreibung */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Einheitenbeschreibung ${index + 1}`, `units[${index}].unitDescriptor`, (
                        <div className="w-full">
                          <SimpleDropdownPopup
                            category="unitDescriptor"
                            value={unit.unitDescriptor || ''}
                            onChange={(value: string) => updateUnit(index, { unitDescriptor: value })}
                            label=""
                            placeholder="Einheit..."
                            allowAdminMode={!isSupplierMode}
                          />
                        </div>
                      ))}
                    </td>
                    
                    {/* Verpackungseinheit */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Verpackungseinheit ${index + 1}`, `units[${index}].packagingUnit`, (
                        <div className="w-full">
                          <SimpleDropdownPopup
                            category="packagingUnit"
                            value={unit.packagingUnit || ''}
                            onChange={(value: string) => updateUnit(index, { packagingUnit: value })}
                            label=""
                            placeholder="Verpackung..."
                            allowAdminMode={!isSupplierMode}
                          />
                        </div>
                      ))}
                    </td>
                    
                    {/* Menge */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Menge ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].quantityOfBaseUnits`, (
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={unit.quantityOfBaseUnits === '' || unit.quantityOfBaseUnits === undefined || unit.quantityOfBaseUnits === null ? '0' : unit.quantityOfBaseUnits}
                          onChange={(e) => updateUnit(index, { quantityOfBaseUnits: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded px-2 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      ))}
                    </td>
                    
                    {/* Gewicht */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Gewicht ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].grossWeight`, (
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={unit.grossWeight || ''}
                          onChange={(e) => updateUnit(index, { grossWeight: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded px-2 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      ))}
                    </td>
                    
                    {/* Gewichtseinheit */}
                    <td className="px-1 py-1.5">
                      {renderFieldWithHistory(`Gewichtseinheit ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].grossWeightUnit`, (
                        <select
                          value={unit.grossWeightUnit || 'g'}
                          onChange={(e) => updateUnit(index, { grossWeightUnit: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="g">g</option>
                          <option value="kg">kg</option>
                        </select>
                      ))}
                    </td>
                    
                    {/* Breite */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Breite ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].width`, (
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={unit.width || ''}
                          onChange={(e) => updateUnit(index, { width: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded px-2 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      ))}
                    </td>
                    
                    {/* Höhe */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Höhe ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].height`, (
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={unit.height || ''}
                          onChange={(e) => updateUnit(index, { height: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded px-2 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      ))}
                    </td>
                    
                    {/* Tiefe */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Tiefe ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].depth`, (
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={unit.depth || ''}
                          onChange={(e) => updateUnit(index, { depth: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded px-2 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                      ))}
                    </td>
                    
                    {/* Dimensionseinheit */}
                    <td className="px-2 py-1.5">
                      {renderFieldWithHistory(`Dimensionseinheit ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].dimensionUnit`, (
                        <select
                          value={unit.dimensionUnit || 'cm'}
                          onChange={(e) => updateUnit(index, { dimensionUnit: e.target.value })}
                          className="w-full h-8 border border-gray-300 rounded px-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="mm">mm</option>
                          <option value="cm">cm</option>
                          <option value="m">m</option>
                        </select>
                      ))}
                    </td>
                    
                    {/* Volumen */}
                    <td className="px-2 py-1.5">
                      <div className="h-8 px-2 bg-gray-50 rounded border border-gray-300 flex items-center text-xs text-gray-600 font-mono">
                        {unit.volumeCM3 ? `${Math.round(parseFloat(unit.volumeCM3))} cm³` : '- cm³'}
                      </div>
                    </td>
                    
                    {/* Optionen */}
                    <td className="px-2 py-1.5">
                      <div className="flex justify-center">
                        {renderFieldWithHistory(`Basisartikel ${unit.unitDescriptor || 'Einheit'} ${index + 1}`, `units[${index}].isTradeItemBaseUnit`, (
                          <label className="flex items-center text-xs text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={unit.isTradeItemBaseUnit || false}
                              onChange={(e) => updateUnit(index, { isTradeItemBaseUnit: e.target.checked })}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </label>
                        ))}
                      </div>
                    </td>
                    
                    {/* Aktionen */}
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => deleteUnit(index)}
                        className="inline-flex items-center justify-center w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
                        title="Einheit löschen"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M9 5l7 7-7 7" />
                      </svg>
                      <p className="text-sm font-medium">Keine Einheiten definiert</p>
                      <p className="text-xs">Klicken Sie auf "Hinzufügen" um zu beginnen</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};